import { useEffect, useState } from "react";
import { aircraft as aircraftApi } from "../api/client";
import { altitudeColor } from "../map/icons";
import type { Aircraft } from "../map/types";
import { t } from "../i18n/en";

/**
 * Find an aircraft by callsign, registration, type or hex among everything the server is
 * tracking — not just what's on screen, since "where is DLH3NM?" is usually asked about something
 * you can't see.
 *
 * It asks the server rather than filtering the streamed list. That list is deliberately limited to
 * the viewport (plus a margin), so filtering it answered "no match" for aircraft the radar was
 * tracking perfectly well a few kilometres off-screen — which was the bug that prompted this.
 */
export default function SearchBox({ onPick }: { onPick: (a: Aircraft) => void }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<Aircraft[]>([]);

  useEffect(() => {
    const q = query.trim().toUpperCase();
    if (q.length < 2) {
      setResults([]);
      return;
    }
    let cancelled = false;
    // Debounced: one request once typing pauses, not one per keystroke.
    const timer = setTimeout(() => {
      aircraftApi
        .all()
        .then(({ aircraft }) => {
          if (cancelled) return;
          setResults(
            aircraft
              .filter((a) => [a.flight, a.reg, a.type, a.hex, a.model].some((v) => v?.toUpperCase().includes(q)))
              .slice(0, 6),
          );
        })
        .catch(() => !cancelled && setResults([]));
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [query]);

  if (!open) {
    return (
      <button className="pill pill-button" onClick={() => setOpen(true)} aria-label={t.search.open}>
        ⌕ {t.search.open}
      </button>
    );
  }

  return (
    <div className="search">
      <input
        autoFocus
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder={t.search.placeholder}
        autoCapitalize="characters"
        autoCorrect="off"
        onKeyDown={(e) => e.key === "Escape" && setOpen(false)}
      />
      <button className="icon-button" onClick={() => { setOpen(false); setQuery(""); }} aria-label={t.detail.close}>
        ✕
      </button>
      {query.trim().length >= 2 && (
        <ul className="search-results">
          {results.length === 0 && <li className="dim">{t.search.none}</li>}
          {results.map((a) => (
            <li key={a.hex}>
              <button
                onClick={() => {
                  onPick(a);
                  setOpen(false);
                  setQuery("");
                }}
              >
                <span className="legend-swatch" style={{ background: altitudeColor(a.alt, a.ground) }} />
                <strong>{a.flight ?? a.reg ?? a.hex}</strong>
                <span className="dim">{[a.type, a.reg].filter(Boolean).join(" · ")}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
